import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { partyText } from "../../static/materialLine";
import { PartyOptionProps } from "../../types/materialLine";
import {
  CuttingReceiveDateBy,
  CuttingReceiveListProps,
  CuttingReceiveProps,
  CuttingReceiveSortBy,
  CuttingReceiveSortOrder,
  OpenCuttingOrderProps,
} from "../../types/cuttingReceive";

export interface CuttingReceivesState {
  receives: CuttingReceiveListProps[];
  totalReceives: number;
  currentReceive: CuttingReceiveProps | null;
  currentReceiveLoading: boolean;
  partyOptions: PartyOptionProps[];
  filterPartyOptions: PartyOptionProps[];
  openOrders: OpenCuttingOrderProps[];
  openOrdersLoading: boolean;
  openOrdersRequestId: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: CuttingReceivesState = {
  receives: [],
  totalReceives: 0,
  currentReceive: null,
  currentReceiveLoading: false,
  partyOptions: [],
  filterPartyOptions: [],
  openOrders: [],
  openOrdersLoading: false,
  openOrdersRequestId: null,
  loading: false,
  error: null,
};

const defaultError = "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";

type FetchCuttingReceiveListDataProps = {
  page: number;
  size?: number;
  search?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  party_id?: number | null;
  date_by?: CuttingReceiveDateBy;
  sort_by?: CuttingReceiveSortBy;
  sort_order?: CuttingReceiveSortOrder;
};

export const fetchCuttingReceiveListData = createAsyncThunk(
  "fetchCuttingReceiveListData",
  async (req: FetchCuttingReceiveListDataProps) => {
    let response: any;
    await GET(`/v1/admin/cutting-receives`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchCuttingReceiveDetail = createAsyncThunk(
  "fetchCuttingReceiveDetail",
  async (id: number) => {
    let response: any;
    await GET(`/v1/admin/cutting-receives/${id}`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// The parties with open orders; partyId keeps an edited receive's own party in the list.
export const fetchCuttingReceiveParties = createAsyncThunk(
  "fetchCuttingReceiveParties",
  async (partyId?: number | null) => {
    let response: any;
    await GET(`/v1/admin/cutting-receives/parties`, {
      party_id: partyId || null,
    })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// The list filter's parties: every cutting party, active or not, and every party that has receives.
export const fetchCuttingReceiveFilterParties = createAsyncThunk(
  "fetchCuttingReceiveFilterParties",
  async () => {
    let response: any;
    await GET(`/v1/admin/cutting-receives/parties`, { all: 1 })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// The party's open orders for the receive form; receive_id keeps an edited receive's own orders and leaves out its
// own pieces.
export const fetchCuttingReceiveOpenOrders = createAsyncThunk(
  "fetchCuttingReceiveOpenOrders",
  async (req: { party_id: number; receive_id?: number | null }) => {
    let response: any;
    await GET(`/v1/admin/cutting-receives/orders`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const cuttingReceivesSlice = createSlice({
  name: "cuttingReceives",
  initialState,
  reducers: {
    clearCurrentCuttingReceive: (state) => {
      state.currentReceive = null;
      state.currentReceiveLoading = false;
    },
    clearCuttingReceiveOpenOrders: (state) => {
      state.openOrders = [];
      state.openOrdersLoading = false;
      state.openOrdersRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCuttingReceiveListData.pending, (state) => {
      state.receives = [];
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchCuttingReceiveListData.fulfilled,
      (state, action: any) => {
        state.loading = false;
        if (action.payload.data.status === "success") {
          state.receives = action.payload.data.data.records;
          state.totalReceives = action.payload.data.data.total_records;
          state.error = null;
        } else {
          state.receives = [];
          state.error = action?.payload?.data?.message || defaultError;
        }
      }
    );
    builder.addCase(
      fetchCuttingReceiveListData.rejected,
      (state, action: any) => {
        state.receives = [];
        state.loading = false;
        state.error = action?.payload?.data?.message || defaultError;
      }
    );

    builder.addCase(fetchCuttingReceiveDetail.pending, (state) => {
      state.currentReceiveLoading = true;
      state.currentReceive = null;
    });
    builder.addCase(
      fetchCuttingReceiveDetail.fulfilled,
      (state, action: any) => {
        state.currentReceiveLoading = false;
        if (action.payload.data.status === "success") {
          state.currentReceive = action.payload.data.data;
        }
      }
    );
    builder.addCase(fetchCuttingReceiveDetail.rejected, (state) => {
      state.currentReceiveLoading = false;
      state.currentReceive = null;
    });

    builder.addCase(
      fetchCuttingReceiveParties.fulfilled,
      (state, action: any) => {
        if (action.payload.data.status === "success") {
          state.partyOptions = action.payload.data.data.records.map(
            (party: any) => ({ value: party.id, label: partyText(party) })
          );
        }
      }
    );

    builder.addCase(
      fetchCuttingReceiveFilterParties.fulfilled,
      (state, action: any) => {
        if (action.payload.data.status === "success") {
          state.filterPartyOptions = action.payload.data.data.records.map(
            (party: any) => ({ value: party.id, label: partyText(party) })
          );
        }
      }
    );

    // Only the latest request counts, so a slow answer for a party picked earlier can't overwrite it.
    builder.addCase(fetchCuttingReceiveOpenOrders.pending, (state, action) => {
      state.openOrders = [];
      state.openOrdersLoading = true;
      state.openOrdersRequestId = action.meta.requestId;
    });
    builder.addCase(
      fetchCuttingReceiveOpenOrders.fulfilled,
      (state, action: any) => {
        if (action.meta.requestId !== state.openOrdersRequestId) return;
        state.openOrdersLoading = false;
        state.openOrders =
          action.payload.data.status === "success"
            ? action.payload.data.data.records
            : [];
      }
    );
    builder.addCase(
      fetchCuttingReceiveOpenOrders.rejected,
      (state, action) => {
        if (action.meta.requestId !== state.openOrdersRequestId) return;
        state.openOrdersLoading = false;
      }
    );
  },
});

export const { clearCurrentCuttingReceive, clearCuttingReceiveOpenOrders } =
  cuttingReceivesSlice.actions;

export const getCuttingReceives = (state: RootState) =>
  state.cuttingReceives.receives;
export const getTotalCuttingReceives = (state: RootState) =>
  state.cuttingReceives.totalReceives;
export const getCurrentCuttingReceive = (state: RootState) =>
  state.cuttingReceives.currentReceive;
export const getCurrentCuttingReceiveLoading = (state: RootState) =>
  state.cuttingReceives.currentReceiveLoading;
export const getCuttingReceivePartyOptions = (state: RootState) =>
  state.cuttingReceives.partyOptions;
export const getCuttingReceiveFilterPartyOptions = (state: RootState) =>
  state.cuttingReceives.filterPartyOptions;
export const getCuttingReceiveOpenOrders = (state: RootState) =>
  state.cuttingReceives.openOrders;
export const getCuttingReceiveOpenOrdersLoading = (state: RootState) =>
  state.cuttingReceives.openOrdersLoading;

export default cuttingReceivesSlice.reducer;
